// src/pages/tour-packages/hooks/useTourPackageEditPage.ts

import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { getAuth, request } from "@/api/client";
import { PATHS } from "@/api/constants";
import { listTourTypes } from "@/api/features/tour-types/api";
import { listSuppliers } from "@/api/features/suppliers/api";
import { useTourDetail } from "@/api/features/tours";

import { useUpdateTourPackage } from "./useUpdateTourPackage";
import { useTourPackageEditForm } from "./useTourPackageEditForm";

import type { UpdateTourWithDetailsPayload } from "@/api/features/tours";

type TourTypeOption = { id: number; name: string };
type SupplierOption = { id: number; name: string };

export function useTourPackageEditPage(tourId: number) {
  const auth = getAuth();
  const user = auth?.user as any;
  const roleName = String(user?.roleName ?? "").trim();
  const isSupplier = roleName === "Supplier";
  const supplierId = Number(user?.supplierId ?? 0);
  const supplierName =
    String(
      user?.supplierCompanyName ??
        user?.username ??
        "My Supplier Account"
    ).trim() || "My Supplier Account";

  /* -----------------------------
   * 1) Load master data
   * --------------------------- */

  const {
    data: tourTypesData,
    isLoading: isLoadingTourTypes,
    error: tourTypesError,
  } = useQuery({
    queryKey: ["tour-types", "edit-page", isSupplier ? "supplier" : "admin"],
    queryFn: async () => {
      if (isSupplier) {
        const res = await request<any>({
          method: "GET",
          url: `${PATHS.tours.base}/public/filters`,
        });
        const data = res?.data ?? res;
        const types = Array.isArray(data?.tourTypes) ? data.tourTypes : [];
        return {
          items: types.map((tt: any) => ({
            id: Number(tt?.id),
            name: String(tt?.name ?? ""),
          })),
        };
      }
      return listTourTypes({ page: 1, limit: 100 });
    },
  });

  const {
    data: suppliersData,
    isLoading: isLoadingSuppliers,
    error: suppliersError,
  } = useQuery({
    queryKey: ["suppliers", "edit-page", isSupplier ? supplierId : "all"],
    queryFn: async () => {
      if (isSupplier) {
        return {
          items:
            supplierId > 0
              ? [{ id: supplierId, supplierCompanyName: supplierName }]
              : [],
        };
      }
      return listSuppliers({
        page: 1,
        limit: 100,
        includeDeleted: false,
      });
    },
  });

  const {
    data: tourDetail,
    isLoading: isLoadingTour,
    error: tourError,
  } = useTourDetail(tourId, true);

  const tourTypes: TourTypeOption[] = useMemo(
    () =>
      (tourTypesData?.items ?? []).map((tt: any) => ({
        id: tt.id,
        name: tt.name,
      })),
    [tourTypesData?.items]
  );

  const suppliers: SupplierOption[] = useMemo(
    () =>
      (suppliersData?.items ?? []).map((s: any) => ({
        id: s.id,
        name: s.supplierCompanyName ?? s.companyName ?? "",
      })),
    [suppliersData?.items]
  );

  const isInitializing =
    isLoadingTourTypes || isLoadingSuppliers || isLoadingTour;

  const initialLoadError = tourTypesError ?? suppliersError ?? tourError ?? null;

  /* -----------------------------
   * 2) Update mutation + local error state
   * --------------------------- */

  const [saveError, setSaveError] = useState<Error | null>(null);

  const updateTourMutation = useUpdateTourPackage(tourId, {
    onError: (error) => setSaveError(error),
  });

  /* -----------------------------
   * 3) Form hook (EDIT) – owns mapping + hydration
   * --------------------------- */

  const formState = useTourPackageEditForm({
    defaultValues: tourDetail ?? null,
    debug: true, // set false anytime
    onSubmit: async (payload: UpdateTourWithDetailsPayload) => {
      // eslint-disable-next-line no-console
      // console.log("[EDIT_PAGE] submit payload", payload);
      // eslint-disable-next-line no-console
      // console.log("[EDIT_PAGE] submit departures", (payload as any)?.departures);

      return updateTourMutation.mutateAsync(payload);
    },
  });

  /* -----------------------------
   * 4) Expose everything for page
   * --------------------------- */

  return {
    ...formState,

    tourTypes,
    suppliers,
    supplierLocked: isSupplier,

    isInitializing,
    initialLoadError,

    isSaving: updateTourMutation.isPending,
    saveError,

    tourDetail: tourDetail ?? null,
  };
}
